/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSaveTransaction';

const ActionSaveTransactionManager: ActionManagerFn<'ActionSaveTransaction'> = (
  platform: Platform,
): ActionManager<'ActionSaveTransaction'> => {
  return {
    ...baseActionManager(platform, 'ActionSaveTransaction'),
    defaultValue,
    props: {
      ...baseActionProps,
      transactionCode: {
        label: 'Transaction Code',
        type: 'string',
        required: true,
      },
      silent: {
        label: 'Silent',
        type: 'boolean',
        helpText:
          "Check this to prevent the message 'No changes to save!' when the user perform this action without making any changes.",
      },
      consolidatedFeedback: {
        label: 'Consolidated Feedback Message',
        type: 'string',
        helpText:
          'By default feedback message will be shown for the respective data store. Specify a consolidated message here if you want to display just a single message instead of one per data store.',
      },
    },
    name: 'Save Transaction',
    getDisplayText: (item) => item.props.transactionCode,
    description: `**Overview**<br/>
The Save Transaction action is an advanced persistence component designed to handle multi-store data commits. Unlike the standard "Save Data Store" action which targets a single alias, this component triggers a save operation for all Data Stores that are grouped under a specific "Transaction Code."<br/><br/>
This action is essential for complex forms or master-detail interfaces where data is spread across multiple stores (e.g., an "Order" header store and an "Order Items" detail store) but must be saved together as a single atomic unit. If any part of the transaction fails, the entire operation can be rolled back, ensuring data integrity across the application.

**Key Features**<br/>
* **Atomic Multi-Store Commit:** Saves changes from multiple Data Stores simultaneously, provided they are all configured with the same Transaction Code.
* **Unified Feedback:** Features a consolidatedFeedback property, allowing developers to replace the barrage of individual "Saved Successfully" messages (one per store) with a single, clean summary message for the user.
* **Transaction Grouping:** Relies on a shared transactionCode string to identify which stores belong to the operation, decoupling the save logic from specific store aliases.
* **Silent Mode:** Includes a silent property to suppress "No changes to save!" warnings, improving the user experience during auto-save or optional-edit workflows.

**Properties**<br/>
* **Transaction Code (Required):** A string property.
  * *Function:* The unique identifier linking multiple Data Stores together. This action will save every store on the page that shares this code.
* **Silent:** A boolean property.
  * *Function:* If enabled, prevents the system from showing a warning message if none of the participating stores have any changes.
* **Consolidated Feedback Message:** A string property.
  * *Function:* A custom success message (e.g., "Order #123 Saved Successfully") that overrides individual store notifications.
  * *Use Case:* Prevents the UI from being cluttered with three separate "Saved" toasts when saving a complex record.

**Use Cases**<br/>
* **Master-Detail Forms:** Saving an Invoice (Header Store) and its Line Items (Detail Store) simultaneously with a single "Save Invoice" button.
* **Complex Wizards:** A multi-step registration process where "Personal Info," "Address," and "Preferences" are kept in separate stores but committed together at the final step.
* **Coordinated Updates:** Saving changes to both the "Project" settings and the "Team Members" list in one click.`,
  };
};

export default ActionSaveTransactionManager;
